"use client";

import {
  RESOURCE_TABLES,
  useCyclingPendingResource,
  usePendingSyncResources,
  useResourceRowCount,
} from "@stockline/web";
import { Check } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

type CyclingResourceLabelProps = Readonly<{
  activeWaveResources: readonly string[];
}>;

/**
 * Build the primary status line. Three states:
 * - no active resource (wave done): "Finalizing…"
 * - active resource + count known: "Loading orders (5,000 already loaded)"
 * - active resource, count not yet known: "Loading orders…"
 */
function buildPrimaryLine(args: {
  current: string | null;
  count: number | null;
  resourceLabel: string;
  formatter: Intl.NumberFormat;
  t: ReturnType<typeof useTranslation>["t"];
}): string {
  const { current, count, resourceLabel, formatter, t } = args;
  if (!current) {
    return t("common.boot.finalizing", { defaultValue: "Finalizing…" });
  }
  if (count != null && count > 0) {
    return t("common.boot.loadingWithCount", {
      formatted: formatter.format(count),
      resource: resourceLabel,
      defaultValue: `Loading ${resourceLabel} (${formatter.format(count)} already loaded)`,
    });
  }
  return t("common.boot.loading", {
    resource: resourceLabel,
    defaultValue: `Loading ${resourceLabel}…`,
  });
}

/**
 * Live status block below the stepper. Two lines:
 *
 *   Loading orders (5,000 already loaded)         ← cycling, with count
 *   ✓ products · units · partners · prices         ← completed in this wave
 *
 * Counts come from Dexie via `useResourceRowCount`, resolved through
 * `RESOURCE_TABLES` because most resource names are kebab-case while
 * their tables are camelCase (`preOrderFlows`, `partnerAccounts`, …).
 * Passing the kebab name directly would silently return null and the
 * UI would stay stuck on "Loading X…" without the count.
 *
 * Plural/gender agreement uses locale-invariant forms (FR/ES
 * `disponibles`, same word for masculine and feminine plural), so the
 * sentence stays grammatical for every resource regardless of noun
 * gender.
 */
export function CyclingResourceLabel({
  activeWaveResources,
}: CyclingResourceLabelProps) {
  const { t, i18n } = useTranslation();
  const { current } = useCyclingPendingResource(activeWaveResources);
  const pending = usePendingSyncResources(activeWaveResources);

  // Resolve resource name → Dexie table name. Without this, configs
  // whose tableName differs from name (preOrderFlows, partnerAccounts,
  // chatSessions, …) silently return null counts.
  const currentTable = current ? (RESOURCE_TABLES[current] ?? current) : "";
  const currentCount = useResourceRowCount(currentTable);

  const formatter = useMemo(
    () => new Intl.NumberFormat(i18n.language),
    [i18n.language],
  );

  // Resource-name translation lives in `common.boot.resources.<name>` for
  // each supported locale. Kebab-to-space is the safety-net fallback.
  const translateResource = (name: string) =>
    t(`common.boot.resources.${name}`, {
      defaultValue: name.replaceAll("-", " "),
    });

  const resourceLabel = current ? translateResource(current) : "";
  const primaryLine = buildPrimaryLine({
    current,
    count: currentCount,
    resourceLabel,
    formatter,
    t,
  });

  // Completed resources in this wave: anything in the input list that
  // isn't pending anymore. Order matches the manifest so the chip list
  // doesn't shuffle as new resources land.
  const pendingSet = new Set(pending);
  const completed = activeWaveResources.filter((r) => !pendingSet.has(r));

  return (
    <div
      className="flex flex-col items-center gap-1.5"
      aria-live="polite"
      role="status"
    >
      <p
        data-testid="global-boot-screen-resource-label"
        className="text-xs text-gray-700 tabular-nums"
        style={{ minHeight: "1.25rem" }}
      >
        {primaryLine}
      </p>
      {completed.length > 0 && (
        <p
          data-testid="global-boot-screen-completed"
          className="flex flex-wrap items-center justify-center gap-x-1 gap-y-0.5 text-[10px] text-emerald-700"
        >
          <Check className="h-2.5 w-2.5" strokeWidth={3} aria-hidden />
          {completed.map((name, i) => (
            <span key={name}>
              {translateResource(name)}
              {i < completed.length - 1 && (
                <span className="ml-1 text-emerald-700/40" aria-hidden>
                  ·
                </span>
              )}
            </span>
          ))}
        </p>
      )}
    </div>
  );
}
